import React, { useCallback, useContext, useEffect, useLayoutEffect } from "react";
import { IFrameContext, Size } from "../../reactcontext/iframe-context";

export function useIFrameResize(): [() => void] {
    const iFrameCtx = useContext(IFrameContext);
    
    const contentChangeTrigger = useCallback(() => {
        if(iFrameCtx)
            iFrameCtx.notifyContentChanged();
    }, [iFrameCtx]);

    React.useLayoutEffect(() => {
        contentChangeTrigger();
    }, [contentChangeTrigger]);

    return [contentChangeTrigger]; // We return the trigger for the old non-functionnal react sub-component that needs a regular function.
}

/**
 * This hook is used to tell the upper most parent that the content should have a certain size but that
 * it cannot see it because our content is displayed as absolute positioning over the top of the parent.
 * We mainly use that for modal popups.
 * @param elRefToFollow 
 * @param widthOffset 
 * @param heightOffset 
 */
export function useIFrameResizeForPopup(elRefToFollow: React.RefObject<HTMLElement>, widthOffset: number = 0, heightOffset: number = 0) {
    const iFrameCtx = useContext(IFrameContext);
    const lastWidth = React.useRef<number>(0);
    const lastHeight = React.useRef<number>(0);

    // Function that serves as a provider for the min size
    const getMinSize = React.useRef<() => Size>(() => {
        return {
            width: lastWidth.current + widthOffset, 
            height: lastHeight.current + heightOffset 
        };
    });

    // Measure the html element and notify the parents
    const updateSizeAndNotify = React.useRef<() => void>(() => {
        lastWidth.current = elRefToFollow.current?.offsetWidth;
        lastHeight.current = elRefToFollow.current?.offsetHeight;
        iFrameCtx.notifyMinSizeChange();
    });

    // On load / On unload
    useEffect(() => {
        // Register our minSize provider
        iFrameCtx.addMinSizeProvider(getMinSize.current);

        // Listen to the content change channel for UI updates from everywhere
        iFrameCtx.onContentChanged(updateSizeAndNotify.current);

        return () => {
            iFrameCtx.removeMinSizeProvider(getMinSize.current);
            iFrameCtx.offContentChanged(updateSizeAndNotify.current);

            // Notify the parent that we are gone (Help the parent returning to its normal size)
            iFrameCtx.notifyMinSizeChange();
        };
    }, [iFrameCtx]);

    // On every changes. In the case the onContentChanged channel would not be used by the caller.
    useLayoutEffect(() => {
        if(iFrameCtx && elRefToFollow.current)
            updateSizeAndNotify.current();
    });
}
